import { memo, useCallback, useMemo } from 'react';
import { RackingArea, RackInsideExtension } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { MixedSwitch } from '@/modules/common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonBoolean } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';

interface Props {
  selectedRackingAreas: RackingArea[];
}

function InsideExtensionComponent({ selectedRackingAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // Shown when any selected rack is one-sided inside; only editable when all of them are
  const insideExtensions = useMemo(
    () =>
      selectedRackingAreas.flatMap(({ insideExtension }) =>
        insideExtension ? [insideExtension] : [],
      ),
    [selectedRackingAreas],
  );

  // null when the racks differ: MixedSwitch shows it as 'Mixed', switching it on sets all
  const forwardParking = getCommonBoolean(
    insideExtensions.map((extension) => extension.forwardParking),
  );

  const onForwardParkingChange = useCallback(
    (newValue: RackInsideExtension['forwardParking']) => {
      fddmSvc.setPreference('rackingArea.insideExtension.forwardParking', newValue);

      const updatedAreas = createEmptyAreas();
      selectedRackingAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          insideExtension: {
            ...area.insideExtension,
            forwardParking: newValue,
          },
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedRackingAreas],
  );

  const onSwitchChange = useCallback(
    (_event: unknown, checked: boolean) => onForwardParkingChange(checked),
    [onForwardParkingChange],
  );

  if (insideExtensions.length === 0) return null;

  const isPartialSelection = insideExtensions.length !== selectedRackingAreas.length;

  return (
    <PropertiesLabel i18nkey='interface:properties.area.forward_parking'>
      <DisabledReasonTooltip
        reasonKey={
          isPartialSelection
            ? 'interface:properties.area.rack.partial_selection.only_inside'
            : undefined
        }
      >
        <MixedSwitch
          checked={forwardParking}
          disabled={isPartialSelection}
          onChange={onSwitchChange}
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

export const InsideExtension = memo(InsideExtensionComponent);
